import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
  TextField,
  Checkbox,
  InputAdornment,
  IconButton,
  Tooltip,
  Link as MuiLink,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  FormHelperText,
  Chip,
  SelectChangeEvent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import GenericTooltip from "../../common/genericToolTip";
import {
  isHealthcareBusinessCategory,
  isAdultFamilyHomeCategoryName,
  formatBusinessCategoryPath,
  getDisplayedBusinessTopLevelCategories,
  getDisplayedBusinessSubcategories,
  isHealthcareCategoryName,
} from "../../../../core/business/category";
import {
  getBusinessSlugAdvisory,
  hasValidBusinessSlugCharacters,
} from "../../../util/businessSlug";

/** Only the fields this accordion reads/writes */
type MinimalForm = {
  name: string;
  url: string;
  description?: string;
  businessCategoryId: number | string | null | undefined;
  businessSubcategoryId?: number | string | null | undefined;
  displayOnBookingPage?: boolean;
};

type BusinessCategory = { id: number | string; name: string };
type ErrorsShape = Record<string, string | undefined>;

export interface BusinessDetailsAccordionProps {
  form: MinimalForm;
  productId?: string | null;
  /** Broad setter so parent can pass setForm<BusinessBasic> safely */
  setForm: React.Dispatch<React.SetStateAction<any>>;
  errors: ErrorsShape;
  setErrors?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  afhLicenseNumber?: string;
  setAfhLicenseNumber?: (value: string) => void;
  afhLicenseError?: string;
  clearAfhLicenseError?: () => void;
  t: (key: string) => string;
  isFormDisabled: boolean;
  rootClientPath: string;
  selectedBusiness?: unknown;
  isDirty: boolean;
  categories: BusinessCategory[];
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCheckBoxStateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  tooltipData: {
    url: string;
    displayOnBookingPage: string;
    businessProfileName: string;
  };
  /** Parent handler is still used for allowed categories */
  handleSelectChange: (e: SelectChangeEvent<string>) => void;
  /** When true, listing must remain private until internal verification. */
  forcePrivateListing?: boolean;
  /** Product extension forms can render this elsewhere. */
  showPublicListingToggle?: boolean;
  showCategoryField?: boolean;
  setIsDirty?: (value: boolean) => void;
}

async function suggestBusinessDescriptionDraft({
  businessName,
  categoryName,
  contextText,
  currentText,
}: {
  businessName?: string;
  categoryName?: string;
  contextText?: string;
  currentText?: string;
}) {
  return String(contextText || currentText || "").trim();
}

export default function BusinessDetailsAccordion({
  form,
  productId,
  setForm,
  errors,
  setErrors,
  afhLicenseNumber = "",
  setAfhLicenseNumber,
  afhLicenseError = "",
  clearAfhLicenseError,
  t,
  isFormDisabled,
  rootClientPath,
  selectedBusiness,
  isDirty,
  categories,
  handleInputChange,
  handleCheckBoxStateChange,
  handleUrlChange,
  tooltipData,
  handleSelectChange,
  forcePrivateListing = false,
  showPublicListingToggle = true,
  showCategoryField = true,
  setIsDirty,
}: BusinessDetailsAccordionProps): JSX.Element {
  const isHealthcareRootCategory = (category: any) => {
    const slug = String(category?.slug || "").toLowerCase();
    return slug === "healthcare" || isHealthcareCategoryName(category?.name);
  };

  const formatCategoryOptionLabel = useCallback((category: any) => {
    if (!isHealthcareBusinessCategory(category)) {
      return formatBusinessCategoryPath(category as any) || String(category?.name || "");
    }
    if (isHealthcareRootCategory(category)) return "Healthcare (General)";
    return String(category?.name || "").trim();
  }, []);

  const selectedCategory = useMemo(() => {
    const effectiveCategoryId =
      form.businessSubcategoryId ?? form.businessCategoryId;
    return categories.find((c) => Number(c.id) === Number(effectiveCategoryId));
  }, [categories, form.businessCategoryId, form.businessSubcategoryId]);
  const topLevelCategories = useMemo(
    () => getDisplayedBusinessTopLevelCategories(categories as any, productId),
    [categories, productId]
  );
  const availableSubcategories = useMemo(
    () =>
      getDisplayedBusinessSubcategories(
        categories as any,
        form.businessCategoryId,
        productId
      ),
    [categories, form.businessCategoryId, productId]
  );
  const selectedTopLevelCategory = useMemo(
    () =>
      topLevelCategories.find(
        (category) => Number(category.id) === Number(form.businessCategoryId)
      ) || null,
    [topLevelCategories, form.businessCategoryId]
  );
  const sortedCategories = useMemo(() => {
    const categoryPath = (category: any) =>
      formatCategoryOptionLabel(category);

    return [...topLevelCategories].sort((left: any, right: any) => {
      const leftRank = isHealthcareBusinessCategory(left)
        ? (isHealthcareRootCategory(left) ? 1 : 0)
        : 2;
      const rightRank = isHealthcareBusinessCategory(right)
        ? (isHealthcareRootCategory(right) ? 1 : 0)
        : 2;

      if (leftRank !== rightRank) return leftRank - rightRank;
      return categoryPath(left).localeCompare(categoryPath(right));
    });
  }, [topLevelCategories, formatCategoryOptionLabel]);
  const sortedSubcategories = useMemo(
    () =>
      [...availableSubcategories].sort((left: any, right: any) =>
        String(left?.name || "").localeCompare(String(right?.name || ""))
      ),
    [availableSubcategories]
  );

  const isAFHSelected = useMemo(
    () => isAdultFamilyHomeCategoryName(String(selectedCategory?.name || "")),
    [selectedCategory]
  );
  const isHealthcareSelected = useMemo(() => {
    const slug = String((selectedTopLevelCategory as any)?.slug || "").toLowerCase();
    return slug === "healthcare" || isHealthcareCategoryName(selectedTopLevelCategory?.name);
  }, [selectedTopLevelCategory]);
  const requiresSubcategory = sortedSubcategories.length > 0;

  useEffect(() => {
    // Care directory visibility rules should not override Booking workspace settings.
    if (productId !== "care") return;
    if (!isHealthcareSelected && form.displayOnBookingPage) {
      setForm((f: any) => ({ ...f, displayOnBookingPage: false }));
    }
  }, [productId, isHealthcareSelected, form.displayOnBookingPage, setForm]);

  useEffect(() => {
    if (!forcePrivateListing) return;
    if (form.displayOnBookingPage) {
      setForm((f: any) => ({ ...f, displayOnBookingPage: false }));
    }
  }, [forcePrivateListing, form.displayOnBookingPage, setForm]);

  // Touched tracking for inline validation
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (k: string) => setTouched((p) => ({ ...p, [k]: true }));

  // Local validity (not gated by touched for header/status)
  const hasText = (v?: string) => !!(v && v.trim().length > 0);
  const nameValid = hasText(form?.name);
  const urlHasText = hasText(form?.url);
  const urlHasValidChars = hasValidBusinessSlugCharacters(form?.url || "");
  const urlValid = urlHasText && urlHasValidChars;
  const categoryValid =
    !showCategoryField ||
    (form?.businessCategoryId != null && String(form.businessCategoryId) !== "");
  const subcategoryValid =
    !requiresSubcategory ||
    (form?.businessSubcategoryId != null && String(form.businessSubcategoryId) !== "");

  // Inline helpers (show after touch, unless server errors present)
  const nameHelper =
    errors?.name ??
    (touched.name && !nameValid ? "Business profile name is required" : "");
  const urlHelper =
    errors?.url ??
    (touched.url && !urlHasText
      ? "Business URL is required"
      : touched.url && !urlHasValidChars
      ? "Only letters, numbers, hyphens (-), underscores (_), and dots (.)"
      : "");
  const urlAdvisory = getBusinessSlugAdvisory(form?.url);

  // Category helper: merge server error and required validation
  const baseCategoryHelper =
    !showCategoryField
      ? ""
      : errors?.businessCategoryId ??
        (touched.businessCategoryId && !categoryValid ? "Category is required" : "");
  const categoryHelper = baseCategoryHelper;
  const subcategoryHelper =
    errors?.businessSubcategoryId ??
    (requiresSubcategory && touched.businessSubcategoryId && !subcategoryValid
      ? "Subcategory is required"
      : "");

  // Header chip (Complete / Some provided / Required)
  const totalRequired = (showCategoryField ? 3 : 2) + (requiresSubcategory ? 1 : 0);
  const validCount = [
    nameValid,
    urlValid,
    !showCategoryField || categoryValid,
    !requiresSubcategory || subcategoryValid,
  ].filter(Boolean).length;
  const headerState =
    validCount === totalRequired
      ? "complete"
      : validCount > 0
        ? "partial"
        : "required";

  // Legacy/server error indicator
  const hasUnsavedErrors = Object.values(errors || {}).some((val) => !!val);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [aiDraft, setAiDraft] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  const handleAiSuggest = async () => {
    setAiLoading(true);
    setAiError("");
    try {
      const text = await suggestBusinessDescriptionDraft({
        businessName: form.name,
        categoryName: selectedCategory?.name,
        contextText: aiContext,
        currentText: form.description || "",
      });
      setAiDraft(text || "");
    } catch (err: any) {
      setAiError(err?.message || "Failed to generate description.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleInsertAiDraft = () => {
    handleInputChange({
      target: { name: "description", value: aiDraft },
    } as React.ChangeEvent<HTMLInputElement>);
    setAiDialogOpen(false);
  };

  return (
    <Accordion defaultExpanded sx={{ mt: 4, borderRadius: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="care-content"
        id="care-header"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <Typography variant="h6">🏢 {t("Business Information")}</Typography>

          {headerState === "complete" && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<CheckCircleRoundedIcon />}
              label={t("Complete")}
            />
          )}
          {headerState === "partial" && (
            <Chip
              size="small"
              color="default"
              variant="outlined"
              icon={<InfoOutlinedIcon />}
              label={t("Some info provided")}
            />
          )}
          {headerState === "required" && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              icon={<ErrorOutlineRoundedIcon />}
              label={t("Business info required")}
            />
          )}

          {hasUnsavedErrors && (
            <ErrorOutlineRoundedIcon color="error" fontSize="small" />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Business Name */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                fullWidth
                disabled={isFormDisabled}
                required
                label={t("Business Profile Name")}
                name="name"
                value={form.name}
                onChange={handleInputChange}
                onBlur={() => markTouched("name")}
                error={Boolean(nameHelper)}
                helperText={nameHelper}
              />
              <Box sx={{ ml: 1 }}>
                <GenericTooltip title={tooltipData.businessProfileName} />
              </Box>
            </Box>
          </Grid>

          {/* Display on Plany365 Care Business List */}
          {showPublicListingToggle && isAFHSelected && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  disabled={isFormDisabled || !selectedBusiness || forcePrivateListing}
                  name="displayOnBookingPage"
                  checked={!!form.displayOnBookingPage}
                  onChange={handleCheckBoxStateChange}
                />
                <span style={{ marginRight: 4 }}>
                  {t("Show on Plany365 Care Business List")}
                </span>
                <GenericTooltip title={tooltipData.displayOnBookingPage} />
                {forcePrivateListing && (
                  <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                    Public listing cannot be enabled until verification is completed.
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* URL */}
          <Grid item xs={12} sm={6}>
            <Box
              display="flex"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={1}
              sx={{ width: "100%", flexDirection: { xs: "column", sm: "row" } }}
            >
              <Typography
                color="textSecondary"
                variant="caption"
                sx={{
                  flexShrink: 0,
                  wordBreak: "break-all",
                  lineHeight: 1.2,
                }}
              >
                {`${rootClientPath}/b/`}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  fullWidth
                  disabled={isFormDisabled}
                  required
                  label={t("Url")}
                  name="url"
                  value={form.url}
                  onChange={handleUrlChange}
                  onBlur={() => markTouched("url")}
                  error={Boolean(urlHelper)}
                  helperText={
                    urlHelper ||
                    t(
                      "Letters, numbers, hyphens (-), underscores (_), dots (.). e.g. acme-inc or Tyber-coaching"
                    )
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {!isDirty && form.url ? (
                          <>
                            <MuiLink
                              href={`${rootClientPath}/b/${form.url}`}
                              target="_blank"
                              rel="noopener"
                              underline="none"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                              }}
                            >
                              <OpenInNew color="action" />
                            </MuiLink>
                            <Tooltip title={t("Copy URL")}>
                              <IconButton
                                onClick={() =>
                                  navigator.clipboard
                                    .writeText(`${rootClientPath}/b/${form.url}`)
                                    .catch(() => alert("Failed to copy"))
                                }
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "not-allowed",
                              color: "text.disabled",
                            }}
                          >
                            <OpenInNew color="disabled" />
                          </Box>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                {!urlHelper && urlAdvisory && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5, color: "warning.main" }}
                  >
                    {urlAdvisory}
                  </Typography>
                )}
              </Box>
              <GenericTooltip title={tooltipData.url} />
            </Box>
          </Grid>

          {showCategoryField ? (
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                disabled={isFormDisabled}
                error={Boolean(categoryHelper)}
                required
              >
                <InputLabel>{t("Category")}</InputLabel>
                <Select
                  name="businessCategoryId"
                  label={t("Category")}
                  value={
                    form.businessCategoryId !== null &&
                    form.businessCategoryId !== undefined
                      ? String(form.businessCategoryId)
                      : ""
                  }
                  onChange={handleSelectChange}
                  onBlur={() => markTouched("businessCategoryId")}
                >
                  <MenuItem value="">
                    <em>{t("Select one")}</em>
                  </MenuItem>

                  {sortedCategories.map((c) => {
                    const isAFH = isAdultFamilyHomeCategoryName(c.name);
                    return (
                      <MenuItem
                        key={String(c.id)}
                        value={String(c.id)}
                      >
                        {isAFH
                          ? `${formatCategoryOptionLabel(c as any) || c.name} (Verification required)`
                          : formatCategoryOptionLabel(c as any) || c.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{categoryHelper}</FormHelperText>
                {isAFHSelected && forcePrivateListing && (
                  <FormHelperText sx={{ color: "text.secondary" }}>
                    Care category is selected. Public listing remains off until verification is completed.
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          ) : null}

          {requiresSubcategory ? (
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                disabled={isFormDisabled}
                error={Boolean(subcategoryHelper)}
                required
              >
                <InputLabel>{t("Subcategory")}</InputLabel>
                <Select
                  name="businessSubcategoryId"
                  label={t("Subcategory")}
                  value={
                    form.businessSubcategoryId !== null &&
                    form.businessSubcategoryId !== undefined
                      ? String(form.businessSubcategoryId)
                      : ""
                  }
                  onChange={(event) => {
                    const nextValue = String(event.target.value || "");
                    setForm((prev: any) => ({
                      ...prev,
                      businessSubcategoryId: nextValue ? Number(nextValue) : null,
                    }));
                    setErrors?.((prev) => {
                      const { businessSubcategoryId, ...rest } = prev;
                      return rest;
                    });
                    markTouched("businessSubcategoryId");
                    setIsDirty?.(true);
                  }}
                  onBlur={() => markTouched("businessSubcategoryId")}
                >
                  <MenuItem value="">
                    <em>{t("Select one")}</em>
                  </MenuItem>
                  {sortedSubcategories.map((subcategory: any) => (
                    <MenuItem key={String(subcategory.id)} value={String(subcategory.id)}>
                      {String(subcategory.name || "")}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {subcategoryHelper || "Choose the specific subcategory for this business."}
                </FormHelperText>
              </FormControl>
            </Grid>
          ) : null}

          {isAFHSelected ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                disabled={isFormDisabled}
                label="AFH License Number"
                placeholder="Example: AFH12345"
                value={afhLicenseNumber}
                onChange={(event) => {
                  setAfhLicenseNumber?.(event.target.value);
                  clearAfhLicenseError?.();
                  setIsDirty?.(true);
                }}
                error={Boolean(afhLicenseError)}
                helperText={
                  afhLicenseError ||
                  "Required for Adult Family Home businesses."
                }
              />
            </Grid>
          ) : null}

          {/* Description */}
          <Grid item xs={12}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary", flex: 1 }}>
                Description
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoFixHighIcon />}
                onClick={() => {
                  setAiDraft(form.description || "");
                  setAiError("");
                  setAiDialogOpen(true);
                }}
              >
                AI Assist
              </Button>
            </Stack>
            <TextField
              fullWidth
              disabled={isFormDisabled}
              label={t("Description")}
              name="description"
              value={form.description ?? ""}
              onChange={handleInputChange}
              multiline
              rows={3}
              error={Boolean(errors.description)}
              helperText={
                errors.description
                  ? errors.description
                  : `${form.description?.length ?? 0}/4000`
              }
            />
          </Grid>

          {/* Footer status like Address/Contact */}
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color={
                headerState === "complete"
                  ? "success.main"
                  : headerState === "partial"
                    ? "text.secondary"
                    : "error.main"
              }
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              {headerState === "complete" && (
                <>
                  <CheckCircleRoundedIcon fontSize="small" />
                  {t("Business info looks good")}
                </>
              )}
              {headerState === "partial" && (
                <>
                  <InfoOutlinedIcon fontSize="small" />
                  {t("Some business info provided")}
                </>
              )}
              {headerState === "required" && (
                <>
                  <ErrorOutlineRoundedIcon fontSize="small" />
                  {t("Please complete all required business fields")}
                </>
              )}
            </Typography>
          </Grid>
        </Grid>

        <Dialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>AI Assist Business Description</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Add a few notes about the business and generate a cleaner public-facing description.
            </Typography>
            <TextField
              label="Context (optional)"
              multiline
              fullWidth
              minRows={3}
              value={aiContext}
              onChange={(e) => setAiContext(e.target.value)}
              placeholder="Example: family-owned, serves seniors, calm environment, strong scheduling and documentation support..."
              sx={{ mb: 2 }}
            />
            <Button variant="outlined" onClick={handleAiSuggest} disabled={aiLoading}>
              {aiLoading ? "Generating..." : "Generate Draft From Context"}
            </Button>
            <TextField
              label="Draft"
              multiline
              fullWidth
              minRows={6}
              value={aiDraft}
              onChange={(e) => setAiDraft(e.target.value)}
              sx={{ mt: 2 }}
            />
            {aiError ? (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {aiError}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAiDialogOpen(false)}>Close</Button>
            <Button variant="contained" onClick={handleInsertAiDraft} disabled={!aiDraft.trim()}>
              Insert Description
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
}
